/**
 * Projection for `IntegrationSectionScene` — the Integration section landing.
 *
 * The demo reached its three integration pages from the left nav and from
 * `show('section','integration')`, which rendered a generic section hero
 * (`SECTINFO`, demo line 1994; `renderSection`, demo line 2994). This transform
 * is that hero's tile list expressed as data, with the counts derived from the
 * seeds and the extractor's lake index rather than transcribed.
 *
 * Pure: no React, no fetching. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/integration/IntegrationSectionScene.tsx
 * @see docs/logic/integration/overview.md
 */

import { AGENTS } from '../../shared/seed/agents';
import { CONNECTORS } from '../../shared/seed/connectors';
import { OEM_STATIC_COUNTS } from '../../shared/oem-counts';

/** One tile on the Integration landing. */
export interface IntegrationCard {
  readonly id: 'data' | 'connectors' | 'agents';
  readonly title: string;
  /** The figure printed beside the title. */
  readonly count: number;
  /** What the figure counts. */
  readonly unit: string;
  /** What the page is for — the demo's own standfirst for that view. */
  readonly blurb: string;
  /** Second line: the shape of what is behind the tile. */
  readonly detail: string;
  readonly to: string;
}

/** Connectors whose status is `'Live'` — the demo's own "n live" figure. */
export const LIVE_CONNECTORS = CONNECTORS.filter((c) => c.status === 'Live').length;

/** Agents running at Level 2 (`Automated`) rather than Level 1 (`Assisted`). */
export const AUTOMATED_AGENTS = AGENTS.filter((a) => a.level === 'L2').length;

/**
 * The native file formats the store holds, as the demo's `#doccount` chip
 * counts them: `'<n> items · <n> connectors · 14 native formats'`
 * (demo line 2415).
 */
export const NATIVE_FORMATS = 14;

/** Demo lines 1451–1453 — the three view standfirsts, as three tiles. */
export function integrationCards(): IntegrationCard[] {
  return [
    {
      id: 'data',
      title: 'Data',
      count: OEM_STATIC_COUNTS.data,
      unit: 'files',
      blurb:
        'Everything the workspace and the agents read from, organised by the system it came ' +
        'through, in the format that system produces.',
      detail: `Grouped by connector · ${NATIVE_FORMATS} native formats · tables, documents, email, transcripts and machine logs in one store`,
      to: '/integration/data',
    },
    {
      id: 'connectors',
      title: 'Connectors',
      count: OEM_STATIC_COUNTS.connectors,
      unit: 'systems',
      blurb:
        "The systems the workspace links into — the OEM's own, the customers' and the third " +
        'parties\'. Each card says what it feeds and how fresh it is.',
      detail: `${LIVE_CONNECTORS} live · the rest arrive nightly, at shift end or on change`,
      to: '/connectors',
    },
    {
      id: 'agents',
      title: 'Agents',
      count: OEM_STATIC_COUNTS.agents,
      unit: 'specialists',
      blurb:
        'Sixteen specialists behind one assistant, each with a defined input, a defined output ' +
        'and a point where a person approves.',
      detail: `${AUTOMATED_AGENTS} at Level 2 · ${OEM_STATIC_COUNTS.agents - AUTOMATED_AGENTS} at Level 1 · nothing runs autonomously`,
      to: '/agents',
    },
  ];
}

/**
 * Demo line 1452, the `.note` under the connector grid — the sentence the whole
 * section exists to make, verbatim.
 */
export const ANY_SYSTEM_NOTE =
  'The point is that any system with an export, an API or a mailbox can be a source.';

/**
 * Demo line 1452, the rest of that note. Kept separate because the first
 * sentence is the headline and this is the qualification under it.
 */
export const FORMATS_NOTE =
  'Sources are represented by the export formats each package actually produces (Maximo MIF ' +
  'XML and OSLC JSON, Pitram XML, SAP JSON, MQTT and RMS JSON-lines, CAN bus logs, parts-book ' +
  'and bulletin PDFs, Excel exports, .eml, .vtt, .docx, plain-text notes). Direct API ' +
  'integration is a later backend phase; the agent contracts do not change.';
